import { gql } from '@apollo/client';

export interface QuickSwitchEvent {
  eid: string;
  to: string;
  title: string;
  organizationName?: string;
  thumbnail?: string;
  muxStreamUrl?: string;
  isLive: boolean;
  highPriority: boolean;
  /** Kept for ordering the list and for dropping favorites that already ended. */
  startTime?: string;
  endTime?: string;
}

export const QUICK_SWITCH_FETCH_POLICY = 'no-cache' as const;

/*
 * How many events a feed asks for. Capped so a heavy day cannot turn one hover into a wall of cards and
 * their players; `total` tells the list how many it is not showing.
 */
export const QUICK_SWITCH_LIMIT = 30;

/*
 * `total` from the last fetch of each feed. Kept outside the components for the same reason the rows are
 * (see `useSocketFeed`): the rail remounts its feeds on every hover, and the count must not blink.
 */
const totals = new Map<string, number>();

export const rememberTotal = (scope: string, total?: number | null): void => {
  if (typeof total === 'number') {
    totals.set(scope, total);
  } else {
    totals.delete(scope);
  }
};

export const recallTotal = (scope: string): number | undefined => totals.get(scope);

export const QUICK_SWITCH_EVENT_FIELDS = gql`
  fragment QuickSwitchEventFields on AssetWithEvent {
    eid
    name
    backgroundUrl
    muxStreamUrl
    has_asset
    isLive
    high_priority
    startTime
    endTime
    client {
      organizationName
      backgroundUrl
    }
  }
`;

export interface RawQuickSwitchEvent {
  eid: string;
  name?: string | null;
  backgroundUrl?: string | null;
  muxStreamUrl?: string | null;
  has_asset?: boolean | null;
  isLive?: boolean | null;
  high_priority?: boolean | null;
  startTime?: string | null;
  endTime?: string | null;
  client?: {
    organizationName?: string | null;
    backgroundUrl?: string | null;
  } | null;
}

export const isShownQuickSwitchEvent = (e: RawQuickSwitchEvent | null): e is RawQuickSwitchEvent =>
  Boolean(e?.eid) && Boolean(e?.has_asset);

export const mapQuickSwitchEvent = (e: RawQuickSwitchEvent): QuickSwitchEvent => ({
  eid: e.eid,
  to: `/event/${e.eid}`,
  title: e.name ?? 'Untitled',
  organizationName: e.client?.organizationName ?? undefined,
  thumbnail: e.backgroundUrl ?? e.client?.backgroundUrl ?? undefined,
  muxStreamUrl: e.muxStreamUrl ?? undefined,
  isLive: Boolean(e.isLive),
  highPriority: Boolean(e.high_priority),
  startTime: e.startTime ?? undefined,
  endTime: e.endTime ?? undefined
});
